/**
 * `@kitup/brand-mylore/legal` — the web-only half of the brand.
 *
 * Separate from the root export because everything here is JSX, and the API
 * imports the root. See `LegalConfig` in `@kitup/brand`.
 */
import type { LegalConfig } from '@kitup/brand';
import { myloreLegal } from './legal-data.js';
import { myloreDocuments } from './documents.js';

export { myloreLegal, myloreDocuments };

const { operator } = myloreLegal;

/**
 * MyLore's legal bundle — a web-only export.
 *
 * Kept out of `BrandConfig` on purpose: the documents are JSX, and the API
 * imports the brand. Anything reachable from `brand` is loaded by the API
 * process, and React has no business being there.
 */
export const legal: LegalConfig = {
  indexTitle: 'Юридическая информация и реквизиты',
  indexIntro: () => (
    <p>
      Сервис «{myloreLegal.serviceName}» создаёт персонализированные электронные детские книги по
      заданию пользователя с применением генеративных технологий. Здесь собраны документы об
      условиях заказа, оплате, обработке материалов и публикации готовой книги.
    </p>
  ),
  indexOutro: () => (
    <p>
      Оферта регулирует договор с заказчиком. Политика описывает общий порядок обработки
      персональных данных. Согласия на фотографии, голос и публикацию являются самостоятельными
      документами и не заменяют друг друга. Правила оплаты и возвратов детализируют Оферту.
    </p>
  ),
  indexOutroTitle: 'Как устроен комплект',
  quarantineTerm: myloreLegal.quarantineTerm,
  documentVersion: myloreLegal.documentVersion,
  documentVersionId: myloreLegal.documentVersionId,
  requisitesTitle: 'Исполнитель и оператор персональных данных',
  requisites: [
    { label: 'Наименование', value: operator.fullName },
    { label: 'ИНН', value: operator.inn },
    { label: 'ОГРНИП', value: operator.ogrnip },
    { label: 'Сайт', value: myloreLegal.siteUrl, href: myloreLegal.siteUrl },
    { label: 'Электронная почта', value: operator.email, href: `mailto:${operator.email}` },
    { label: 'Телефон', value: operator.phone, href: operator.phoneHref },
    { label: 'Почтовый адрес', value: operator.postalAddress },
  ],
  footerContacts: [
    { icon: 'phone', label: operator.phone, href: operator.phoneHref },
    { icon: 'email', label: operator.email, href: `mailto:${operator.email}` },
    {
      icon: 'telegram',
      label: myloreLegal.socials.telegram.handle,
      href: myloreLegal.socials.telegram.href,
    },
    {
      icon: 'instagram',
      label: myloreLegal.socials.instagram.handle,
      href: myloreLegal.socials.instagram.href,
    },
  ],
  documents: myloreDocuments,
  // Values copied verbatim from the dictionaries, including grammatical cases.
  documentLinks: {
    registerTerms: { ru: 'публичную оферту', en: 'public offer' },
    paywallTerms: { ru: 'условия оферты', en: 'public offer' },
    paywallRefund: { ru: 'оплаты и возвратов', en: 'payment and refund rules' },
    pricingRules: { ru: 'в правилах заказа', en: 'order rules' },
  },
  routes: {
    terms: '/legal/offer',
    privacy: '/legal/privacy',
    payment: '/legal/payment',
    photoConsent: '/legal/photo-consent',
    voiceConsent: '/legal/voice-consent',
    publicationConsent: '/legal/publication-consent',
  },
  footerLinks: [
    { label: 'Тарифы', to: '/pricing' },
    { label: 'Оферта', to: '/legal/offer' },
    { label: 'Персональные данные', to: '/legal/privacy' },
    { label: 'Оплата и возвраты', to: '/legal/payment' },
    { label: 'Cookie', to: '/legal/cookies' },
    { label: 'Реквизиты', to: '/legal' },
  ],
  ui: {
    backHome: '← На главную',
    allDocuments: 'Все документы и реквизиты',
    documentsTitle: 'Документы',
    editionPrefix: 'Редакция от',
    versionPrefix: 'версия',
    footerNavLabel: 'Информация о сервисе',
  },
};
